import { Vector3, AbstractMesh } from '@babylonjs/core';

/**
 * Collision groups (bitmask)
 */
export enum CollisionGroups {
  STATIC = 0x01,      // Level geometry
  DYNAMIC = 0x02,     // Crates, props, destructibles
  CHARACTER = 0x04,   // Player, NPCs
  PROJECTILE = 0x08,  // Weapons, bullets
  PARTICLE = 0x10     // Liquid particles
}

/**
 * Collision masks - which groups can collide with which
 */
export const CollisionMasks = {
  STATIC: 0xFF,                                      // Collides with all
  DYNAMIC: CollisionGroups.STATIC | CollisionGroups.DYNAMIC | CollisionGroups.CHARACTER, // Collides with static, dynamic, and characters
  CHARACTER: CollisionGroups.STATIC | CollisionGroups.CHARACTER | CollisionGroups.DYNAMIC, // Collides with static, characters, and dynamic
  PROJECTILE: CollisionGroups.STATIC | CollisionGroups.CHARACTER,
  PARTICLE: CollisionGroups.STATIC                   // Only environment
};

/**
 * Physics shape types
 */
export enum PhysicsShapeType {
  BOX = 'box',
  SPHERE = 'sphere',
  CAPSULE = 'capsule',
  CYLINDER = 'cylinder',
  MESH = 'mesh',
  CONVEX_HULL = 'convex_hull'
}

/**
 * Physics body configuration
 */
export interface PhysicsBodyConfig {
  mass: number;
  friction?: number;
  restitution?: number;
  linearDamping?: number;
  angularDamping?: number;
  isKinematic?: boolean;
  isTrigger?: boolean;
  collisionGroup?: number;  // Collision group bitmask
  collisionMask?: number;   // Which groups this body collides with
}

/**
 * Physics shape abstraction
 */
export interface IPhysicsShape {
  type: PhysicsShapeType;
  dispose(): void;
}

/**
 * Physics body abstraction
 */
export interface IPhysicsBody {
  mesh: AbstractMesh;
  
  // Linear velocity
  getLinearVelocity(): Vector3;
  setLinearVelocity(velocity: Vector3): void;
  
  // Angular velocity
  getAngularVelocity(): Vector3;
  setAngularVelocity(velocity: Vector3): void;
  
  // Forces and impulses
  applyForce(force: Vector3, contactPoint?: Vector3): void;
  applyImpulse(impulse: Vector3, contactPoint?: Vector3): void;
  applyTorque(torque: Vector3): void;
  applyAngularImpulse(impulse: Vector3): void;
  
  // Position and rotation
  setPosition(position: Vector3): void;
  setRotation(rotation: Vector3): void;
  
  // Mass and properties
  setMass(mass: number): void;
  setFriction(friction: number): void;
  setRestitution(restitution: number): void;
  
  // Enable/disable
  setEnabled(enabled: boolean): void;
  isEnabled(): boolean;
  
  // Per-body gravity override (useful for characters that use manual gravity)
  setBodyGravity(gravity: Vector3): void;
  
  // Cleanup
  dispose(): void;
}

/**
 * Physics engine abstraction interface
 */
export interface IPhysicsEngine {
  /**
   * Initialize the physics engine
   */
  initialize(gravity: Vector3): Promise<void>;
  
  /**
   * Create a physics body for a mesh
   */
  createBody(
    mesh: AbstractMesh,
    shapeType: PhysicsShapeType,
    config: PhysicsBodyConfig
  ): IPhysicsBody;
  
  /**
   * Remove a physics body
   */
  removeBody(body: IPhysicsBody): void;
  
  /**
   * Set gravity
   */
  setGravity(gravity: Vector3): void;
  
  /**
   * Get current gravity
   */
  getGravity(): Vector3;
  
  /**
   * Update physics simulation (called each frame)
   */
  update(deltaTime: number): void;
  
  /**
   * Clean up and dispose
   */
  dispose(): void;
  
  /**
   * Check if physics is ready
   */
  isReady(): boolean;
  
  /**
   * Get the underlying physics plugin (for advanced use)
   */
  getPlugin(): any;

  /**
   * Update collision filtering for an existing body.
   */
  setCollisionFilter(body: IPhysicsBody, collisionGroup?: number, collisionMask?: number): void;

  /**
   * Create a static triangle mesh collider directly from mesh vertex data.
   * This is for static collision geometry from imported GLB files where the standard
   * PhysicsImpostor approach may fail.
   * Returns null if the mesh has no valid vertex data.
   */
  createStaticTriangleMesh?(
    mesh: AbstractMesh,
    config: PhysicsBodyConfig
  ): IPhysicsBody | null;

  /**
   * Perform a physics raycast and return hit information.
   * Returns null if no hit.
   */
  raycast?(
    from: Vector3,
    to: Vector3,
    collisionMask?: number
  ): { hitPoint: Vector3; hitNormal: Vector3; distance: number } | null;
}
